import { Injectable, inject } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MainNavigationService } from '../main-navigation.service';
import { NGXLogger } from 'ngx-logger';

export interface TeaByFlavourResolverData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  teaFlavour: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  teas: any[];
  errors: string[];
  headerImageURL: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class TeaByFlavourResolver implements Resolve<TeaByFlavourResolverData> {
  private mainNavigationService = inject(MainNavigationService);
  private logger = inject(NGXLogger);

  private imageMap = {
    "sweet-honey": "honey-sweet.jpg",
    "fruity": "fruity-banner.jpg",
    "floral": "floral-banner.jpg",
    "malty-chocolate": "malty-chocolate-banner.jpg",
    "roasty": "roasty-banner.jpg",
    "nutty": "nutty-banner.jpg",
    "umami": "umami-banner.jpg",
    "mineral": "mineral-banner.jpg",
    "earthy-woody": "earthy-woody-banner.jpg",
    "smokey": "smokey-banner.jpg",
    "spice": "spice-banner.jpg", 
    "marine-seaweed": "seaweed-banner.jpg",
    "vegetal-grassy": "grassy-banner.jpg",
    "smooth-creamy": "creamy-banner.jpg"
  };

  resolve(route: ActivatedRouteSnapshot): Observable<TeaByFlavourResolverData> {
    const flavourSlug = route.params['tea-flavour-slug'];
    
    if (!flavourSlug) {
      return of({
        teaFlavour: null,
        teas: [],
        errors: ['Tea flavour slug not found in route'],
        headerImageURL: null
      });
    }

    // Determine header image URL
    const imageName = this.imageMap[flavourSlug as keyof typeof this.imageMap];
    const headerImageURL = imageName ? `/assets/flavour-header/${imageName}` : null;

    // Combine both API calls
    return combineLatest([
      this.mainNavigationService.getTeaFlavour(flavourSlug).pipe(
        catchError(error => {
          this.logger.error('An error occurred while retrieving the tea flavour:', error);
          return of(null);
        })
      ),
      this.mainNavigationService.getTeasByFlavour(flavourSlug).pipe(
        catchError(error => {
          this.logger.error('An error occurred while retrieving the teas:', error);
          return of([]);
        })
      )
    ]).pipe(
      map(([teaFlavour, teas]) => {
        const errors: string[] = [];
        
        if (!teaFlavour) {
          errors.push('An error occurred while retrieving the tea flavour.');
        }
        
        if (!Array.isArray(teas) || teas.length === 0) {
          // Only add error if we expected teas but got none due to an error
          if (!Array.isArray(teas)) {
            errors.push('An error occurred while retrieving the teas.');
          }
        }

        return {
          teaFlavour,
          teas: Array.isArray(teas) ? teas : [],
          errors,
          headerImageURL
        };
      })
    );
  }
}
